import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Share2 } from 'lucide-react';
import { shareElementAsImage } from '../utils/exportUtils';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ChampionTitles = ({ userId }) => {
  const { API } = useContext(AuthContext);
  const [titles, setTitles] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [playerAvatar, setPlayerAvatar] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareTitle, setShareTitle] = useState(null);
  const [sharingId, setSharingId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/users/${userId}/titles`),
      axios.get(`${API}/users/${userId}`).catch(() => ({ data: {} })),
    ])
      .then(([t, u]) => {
        setTitles(t.data.titles || []);
        setPlayerName(u.data.full_name || '');
        setPlayerAvatar(u.data.avatar || '');
      })
      .catch(() => setTitles([]))
      .finally(() => setLoading(false));
  }, [API, userId]);

  const handleShare = async (e, t) => {
    e.preventDefault();
    e.stopPropagation();
    setShareTitle(t);
    setSharingId(t.tournament_id);
    await new Promise((r) => setTimeout(r, 250));
    try {
      const el = document.getElementById('title-share-card');
      const res = await shareElementAsImage(el, {
        filename: `champion-${(playerName || 'tennis').replace(/\s+/g, '-')}.png`,
        title: `🏆 ${playerName} - אלוף ${t.name}`,
      });
      toast.success(res === 'shared' ? 'התמונה שותפה!' : 'התמונה הורדה - שתף אותה בוואטסאפ/אינסטגרם 🎾');
    } catch (err) {
      console.error(err);
      toast.error('שגיאה בשיתוף');
    } finally {
      setShareTitle(null);
      setSharingId(null);
    }
  };

  if (loading || titles.length === 0) return null;

  return (
    <Card className="mb-6 border-2 border-amber-200 bg-gradient-to-l from-amber-50 to-yellow-50" dir="rtl" data-testid="champion-titles-card">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <Trophy className="w-6 h-6 text-amber-500" />
          אליפויות
          <span className="inline-flex items-center justify-center min-w-7 h-7 px-2 rounded-full bg-amber-500 text-white text-sm font-black">
            {titles.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {titles.map((t) => (
            <div
              key={t.tournament_id}
              data-testid={`title-${t.tournament_id}`}
              className="group relative flex items-center gap-3 rounded-xl border-2 border-amber-200 bg-white p-3 transition-all hover:shadow-md"
            >
              <Link to={`/tournament/${t.tournament_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-inner">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-slate-900 truncate">🏆 {t.name}</div>
                  <div className="text-xs text-slate-500">
                    {t.type === 'daily' ? 'תחרות יומית' : 'נוקאאוט'}
                    {t.category ? ` · ${t.category}` : ''}
                    {t.date ? ` · ${fmtDate(t.date)}` : ''}
                  </div>
                </div>
              </Link>
              <button
                onClick={(e) => handleShare(e, t)}
                data-testid={`share-title-${t.tournament_id}`}
                title="שתף הישג"
                disabled={sharingId === t.tournament_id}
                className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 hover:bg-amber-500 hover:text-white transition-colors disabled:opacity-50"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </CardContent>

      {/* Off-screen share poster — 9:16 story format (Instagram/WhatsApp) */}
      {shareTitle && (
        <div
          id="title-share-card"
          style={{
            position: 'fixed', left: '-99999px', top: 0, width: '540px', height: '960px',
            background: 'radial-gradient(120% 80% at 50% 0%, #10402f 0%, #0b1220 55%, #060a14 100%)',
            direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: '64px 44px 48px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
            boxSizing: 'border-box', color: '#fff', overflow: 'hidden',
          }}
        >
          {/* decorative rings */}
          <div style={{ position: 'absolute', top: '-120px', right: '-120px', width: '340px', height: '340px', borderRadius: '50%', border: '2px solid rgba(52,211,153,0.18)' }} />
          <div style={{ position: 'absolute', bottom: '-140px', left: '-100px', width: '300px', height: '300px', borderRadius: '50%', border: '2px solid rgba(251,191,36,0.15)' }} />

          <div style={{ textAlign: 'center', zIndex: 2 }}>
            <div style={{ fontSize: '22px', fontWeight: 900, letterSpacing: '4px', color: '#34d399' }}>TENNIS·PRO ISRAEL</div>
            <div style={{ width: '60px', height: '4px', background: '#fbbf24', margin: '14px auto 0', borderRadius: '4px' }} />
          </div>

          <div style={{ textAlign: 'center', zIndex: 2 }}>
            <div style={{ fontSize: '90px', lineHeight: 1 }}>🏆</div>
            <div style={{ fontSize: '30px', fontWeight: 900, color: '#fbbf24', marginTop: '10px', letterSpacing: '1px' }}>אלוף התחרות</div>

            <div style={{
              width: '220px', height: '220px', borderRadius: '50%', margin: '32px auto 0',
              border: '7px solid #fbbf24', overflow: 'hidden', background: '#fbbf24',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 12px rgba(251,191,36,0.12)',
            }}>
              {playerAvatar ? (
                <img src={playerAvatar} alt="" crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '96px', fontWeight: 900, color: '#0b1220' }}>
                  {playerName?.charAt(0) || '?'}
                </span>
              )}
            </div>
            <div style={{ fontSize: '46px', fontWeight: 900, marginTop: '28px' }}>{playerName}</div>
            <div style={{ fontSize: '25px', color: '#fcd34d', fontWeight: 800, marginTop: '14px' }}>
              🏆 {shareTitle.name}
            </div>
            <div style={{ fontSize: '18px', color: '#94a3b8', marginTop: '12px' }}>
              {(shareTitle.type === 'daily' ? 'תחרות יומית' : 'נוקאאוט')}
              {shareTitle.category ? ` · ${shareTitle.category}` : ''}
              {shareTitle.date ? ` · ${fmtDate(shareTitle.date)}` : ''}
            </div>
          </div>

          <div style={{ textAlign: 'center', zIndex: 2 }}>
            <div style={{ fontSize: '19px', color: '#34d399', fontWeight: 800 }}>#TennisProIsrael</div>
            <div style={{ fontSize: '16px', color: '#64748b', marginTop: '6px' }}>tennisproisrael.co.il</div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ChampionTitles;
